/**
 * Word matching, ranked by how well each word matched — not by where.
 *
 * A palette over hundreds of arc names. Each typed word must match somewhere
 * in the title, in any order, and is scored by its kind of match, best first:
 * the title opens with it, it is a whole word, it starts a word, it sits
 * inside one, and last, its letters are scattered — which is how initials
 * work: `wp` reaches "Web plane". Where in the title a word matched only
 * breaks ties, so "Items live in their folder" ranks with every other title
 * holding the word `folder`, not behind every title that merely starts with
 * an f.
 *
 * Null means no match. A lower score is a better one, so callers sort
 * ascending and never have to remember which way round it goes.
 */
export function fuzzyScore(text: string, query: string): number | null {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  const t = text.toLowerCase();

  let score = 0;
  for (const word of words) {
    const s = wordScore(t, word);
    if (s === null) return null;
    score += s;
  }
  // Shorter titles win ties: with the same words matched, the one carrying
  // less other text is the more likely target.
  return score + t.length / 10_000;
}

const BOUNDARY = /[\s\-—–/·+:,.()'"‘’“”]/;

const startsWord = (t: string, at: number) => at === 0 || BOUNDARY.test(t[at - 1] ?? '');
const endsWord = (t: string, end: number) => end >= t.length || BOUNDARY.test(t[end] ?? '');

/** Tier in whole units; position as a fraction that never crosses a tier. */
function wordScore(t: string, word: string): number | null {
  let best: number | null = null;
  for (let at = t.indexOf(word); at >= 0; at = t.indexOf(word, at + 1)) {
    const opens = at === 0;
    const tier = !startsWord(t, at)
      ? 4
      : endsWord(t, at + word.length)
        ? opens ? 0 : 1
        : opens ? 2 : 3;
    const s = tier + at / 1_000;
    if (best === null || s < best) best = s;
  }
  if (best !== null) return best;

  const scattered = scatteredCost(t, word);
  return scattered === null ? null : 5 + scattered / 100;
}

/**
 * Letters in order, not adjacent. Tried from every place the first letter
 * occurs, keeping the cheapest: a greedy leftmost walk would take the first
 * `m` of "Make the MCP …" and score `mcp` as scattered across the sentence.
 */
function scatteredCost(t: string, word: string): number | null {
  let best: number | null = null;
  for (let start = t.indexOf(word[0]!); start >= 0; start = t.indexOf(word[0]!, start + 1)) {
    let cost = startsWord(t, start) ? 0 : 2;
    let previous = start;
    for (const ch of word.slice(1)) {
      const at = t.indexOf(ch, previous + 1);
      // A later start can only land later, so it cannot succeed either.
      if (at < 0) return best;
      // Adjacency is free and so is landing on a word start — the reason
      // initials work; anything else pays for the letters it skipped.
      if (at !== previous + 1 && !startsWord(t, at)) cost += at - previous + 2;
      previous = at;
    }
    if (best === null || cost < best) best = cost;
  }
  return best;
}

/** Every match, best first — for a list that can show them all. */
export function rankMatches<T>(items: T[], query: string, textOf: (item: T) => string): T[] {
  return items
    .map((item) => ({ item, score: fuzzyScore(textOf(item), query) }))
    .filter((r): r is { item: T; score: number } => r.score !== null)
    .sort((a, b) => a.score - b.score)
    .map((r) => r.item);
}

export function bestMatches<T>(
  items: T[],
  query: string,
  textOf: (item: T) => string,
  limit = 6,
): T[] {
  return rankMatches(items, query, textOf).slice(0, limit);
}
